"use client";

import Link from "next/link";
import Image from "next/image";
import { usePathname } from "next/navigation";
import { ListChecks, Play } from "lucide-react";
import { cn } from "@/components/ui/cn";
import { useDict } from "@/components/i18n/DictProvider";
import type { EventEntity, EventItem } from "@/lib/lecturehub/types";
import {
  isRecording,
  orderedItems,
  recordingSubtitle,
  recordingThumb,
  recordingTitle,
} from "./eventRecordings";

type Props = {
  event: EventEntity | null | undefined;
  /** /lang/library/events/[id]/[slug] — rows link to `${watchBase}/${itemId}`. */
  watchBase: string;
  moduleName: string;
  /** Back-link to the event detail page. */
  detailHref: string;
};

export function EventPlaylistSidebar({ event, watchBase, moduleName, detailHref }: Props) {
  // Active recording comes from the URL (not a prop) so this playlist can live
  // in the persistent [itemId] layout: it stays mounted across recording
  // switches and just moves the highlight — same pattern as LessonSidebar.
  const pathname = usePathname();
  const activeId = pathname.split("/").filter(Boolean).pop() ?? "";
  const items = orderedItems(event);
  const t = useDict();

  return (
    <aside
      data-section="event-playlist"
      className="lg:sticky lg:top-[calc(var(--app-topbar-h)+1rem)]"
    >
      <div
        className="bg-card text-card-foreground rounded-3xl shadow-soft overflow-hidden"
        style={{ boxShadow: "var(--shadow-soft)" }}
      >
        <div className="px-6 py-5 border-b border-border">
          <p className="text-muted-foreground text-[11px] uppercase tracking-wide font-bold mb-1">
            {t.detail.recordings}
          </p>
          <Link
            href={detailHref}
            className="font-display font-bold text-base leading-5 hover:text-accent line-clamp-2"
          >
            {moduleName}
          </Link>
          <p className="mt-3 text-xs text-muted-foreground">
            {items.length} {t.detail.programme.toLowerCase()}
          </p>
        </div>

        <ol className="p-2 max-h-[55vh] lg:max-h-[60vh] overflow-y-auto">
          {items.map((it, i) => (
            <PlaylistRow
              key={it.id}
              item={it}
              index={i}
              active={it.id === activeId}
              watchBase={watchBase}
            />
          ))}
        </ol>
      </div>
    </aside>
  );
}

function PlaylistRow({
  item,
  index,
  active,
  watchBase,
}: {
  item: EventItem;
  index: number;
  active: boolean;
  watchBase: string;
}) {
  const t = useDict();
  const playable = isRecording(item);
  const title = recordingTitle(item);
  const subtitle = recordingSubtitle(item);
  const thumb = recordingThumb(item);

  const inner = (
    <>
      <span className="text-foreground/40 text-[11px] tabular-nums font-display font-extrabold w-5 text-right shrink-0">
        {String(index + 1).padStart(2, "0")}
      </span>
      <div className="relative w-20 aspect-[16/9] rounded-md overflow-hidden bg-muted shrink-0">
        {thumb ? (
          <Image src={thumb.uri} alt={thumb.alt} fill sizes="80px" className="object-cover" />
        ) : (
          <span aria-hidden className="absolute inset-0 grid place-items-center text-accent">
            {playable ? <Play className="size-3.5" /> : <ListChecks className="size-3.5" />}
          </span>
        )}
        {active ? (
          <span aria-hidden className="absolute inset-0 grid place-items-center bg-primary/40">
            <Play className="size-4 fill-current text-white" />
          </span>
        ) : null}
      </div>
      <div className="min-w-0 flex-1">
        <p className="line-clamp-2 font-display font-bold text-[13px] leading-4 text-card-foreground">
          {title}
        </p>
        {subtitle ? (
          <p className="mt-1 text-[11px] text-muted-foreground truncate">{subtitle}</p>
        ) : null}
      </div>
    </>
  );

  const baseRow = "relative flex items-start gap-2.5 p-2 rounded-2xl transition-colors";

  return (
    <li className="mb-1 last:mb-0">
      {playable ? (
        <Link
          href={`${watchBase}/${item.id}`}
          aria-current={active ? "page" : undefined}
          className={cn(
            baseRow,
            active ? "bg-primary/10 border border-primary/20 pl-3" : "hover:bg-muted/60 pl-2",
          )}
        >
          {active ? (
            <span aria-hidden className="absolute inset-y-2 left-0 w-1 rounded-r-full bg-primary" />
          ) : null}
          {inner}
        </Link>
      ) : (
        <div
          aria-disabled="true"
          className={cn(baseRow, "opacity-60 cursor-not-allowed pl-2")}
          title={t.detail.notARecording}
        >
          {inner}
        </div>
      )}
    </li>
  );
}
